import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
   Home,
   Users,
   Activity,
   BarChart3,
   DollarSign,
   UserPlus,
   Shield,
   MapPin,
   Image,
   Settings,
   Menu,
   TrendingUp,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface NavigationItem {
   name: string;
   href: string;
   icon: any;
   roles: string[]; // Array of roles that can see this item
}

const navigationItems: NavigationItem[] = [
   { name: 'Dashboard', href: '/', icon: Home, roles: ['admin', 'super admin'] },
   { name: 'Users', href: '/users', icon: Users, roles: ['admin', 'super admin'] },
   { name: 'Activities', href: '/activities', icon: Activity, roles: ['admin', 'super admin'] },
   { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['admin', 'super admin'] },
   { name: 'Map View', href: '/map', icon: MapPin, roles: ['admin', 'super admin'] },
   { name: 'Financial', href: '/financial', icon: DollarSign, roles: ['super admin'] }, // Only super admin
   { name: 'Invitations', href: '/invitations', icon: UserPlus, roles: ['admin', 'super admin'] },
   { name: 'Admin Members', href: '/admin-members', icon: Shield, roles: ['super admin'] }, // Only super admin
   { name: 'Influencer Members', href: '/influencer-members', icon: UserPlus, roles: ['admin', 'super admin'] },
   { name: 'Trips', href: '/trips', icon: MapPin, roles: ['admin', 'super admin'] },
   { name: 'Photos', href: '/photos', icon: Image, roles: ['admin', 'super admin'] },
   { name: 'Settings', href: '/settings', icon: Settings, roles: ['admin', 'super admin'] },
   { name: 'Influencer', href: '/influencer', icon: TrendingUp, roles: ['admin', 'super admin', 'influencer'] },
];

export const Sidebar = () => {
   const [sidebarOpen, setSidebarOpen] = useState(false);
   const { user } = useAuth();

   // Filter navigation items based on user role
   const navigation = navigationItems.filter((item) => {
      if (!user?.role) return false; // If no role, show nothing
      return item.roles.includes(user.role);
   });

   console.log('🔍 User role:', user?.role);
   console.log(
      '📋 Filtered navigation items:',
      navigation.map((item) => item.name)
   );

  return (
     <>
        {/* Mobile sidebar */}
        <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
           <div className='fixed inset-0 bg-gray-900/80' onClick={() => setSidebarOpen(false)} />
           <div className='fixed inset-y-0 left-0 flex w-72 flex-col bg-gray-900'>
              <div className='flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-800 px-6'>
                 <span className='text-white font-semibold text-lg'>WeTrippen Admin</span>
              </div>
              <nav className='flex flex-1 flex-col px-6 py-4'>
                 <ul role='list' className='flex flex-1 flex-col gap-y-7'>
                    <li>
                       <ul role='list' className='-mx-2 space-y-1'>
                          {navigation.map((item) => (
                             <li key={item.name}>
                                <NavLink
                                   to={item.href}
                                   className={({ isActive }) =>
                                      `group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold ${
                                         isActive
                                            ? 'bg-gray-800 text-white'
                                            : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                      }`
                                   }
                                   onClick={() => setSidebarOpen(false)}
                                >
                                   <item.icon className='h-6 w-6 shrink-0' aria-hidden='true' />
                                   {item.name}
                                </NavLink>
                             </li>
                          ))}
                       </ul>
                    </li>
                    <li className='mt-auto'>
                       <div className='flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-white'>
                          <img
                             className='h-8 w-8 rounded-full bg-gray-800'
                             src={`https://ui-avatars.com/api/?name=${
                                user?.firstName || user?.email.charAt(0)
                             }+${user?.lastName || ''}&background=3b82f6&color=fff`}
                             alt=''
                          />
                          <span className='sr-only'>Your profile</span>
                          <span aria-hidden='true'>
                             {user?.firstName} {user?.lastName}
                          </span>
                       </div>
                    </li>
                 </ul>
              </nav>
           </div>
        </div>

        {/* Desktop sidebar */}
        <div className='hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col'>
           <div className='flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900 px-6 pb-4'>
              <div className='flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-800'>
                 <span className='text-white font-semibold text-lg'>WeTrippen Admin</span>
              </div>
              <nav className='flex flex-1 flex-col'>
                 <ul role='list' className='flex flex-1 flex-col gap-y-7'>
                    <li>
                       <ul role='list' className='-mx-2 space-y-1'>
                          {navigation.map((item) => (
                             <li key={item.name}>
                                <NavLink
                                   to={item.href}
                                   className={({ isActive }) =>
                                      `group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold ${
                                         isActive
                                            ? 'bg-gray-800 text-white'
                                            : 'text-gray-400 hover:text-white hover:bg-gray-800'
                                      }`
                                   }
                                >
                                   <item.icon className='h-6 w-6 shrink-0' aria-hidden='true' />
                                   {item.name}
                                </NavLink>
                             </li>
                          ))}
                       </ul>
                    </li>
                    <li className='mt-auto'>
                       <div className='flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-white'>
                          <img
                             className='h-8 w-8 rounded-full bg-gray-800'
                             src={`https://ui-avatars.com/api/?name=${
                                user?.firstName || user?.email.charAt(0)
                             }+${user?.lastName || ''}&background=3b82f6&color=fff`}
                             alt=''
                          />
                          <span className='sr-only'>Your profile</span>
                          <span aria-hidden='true'>
                             {user?.firstName} {user?.lastName}
                          </span>
                       </div>
                    </li>
                 </ul>
              </nav>
           </div>
        </div>

        {/* Mobile menu button */}
        <div className='sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden'>
           <button
              type='button'
              className='-m-2.5 p-2.5 text-gray-700 lg:hidden'
              onClick={() => setSidebarOpen(true)}
           >
              <span className='sr-only'>Open sidebar</span>
              <Menu className='h-6 w-6' aria-hidden='true' />
           </button>
           <div className='flex-1 text-sm font-semibold leading-6 text-gray-900'>
              WeTrippen Admin
           </div>
        </div>
     </>
  );
}; 